import React, { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import styles from "./DropAndCrop.module.scss";
import { Cropper } from "react-cropper";
import { observer } from "mobx-react";

type TDropAndCrop = {
  onImageAccept: (image: File) => void;
  setError: (value: string) => void;
  image: File | undefined;
  imageSize: number;
  error: string;
};

const DropAndCrop: React.FC<TDropAndCrop> = observer(
  ({ onImageAccept, image, imageSize, error, setError }) => {
    const [imgName, setImgName] = useState();
    const [imgType, setImgType] = useState();
    const [isCrop, setIsCrop] = useState(!!image);
    const [cropper, setCropper] = useState<any>();
    const [onceCropped, setOnceCropped] = useState(false);
    const [initialImage, setInitialImage] = useState<File>(image);

    const onDrop = useCallback((acceptedFiles) => {
      if (acceptedFiles[0]) {
        if (
          acceptedFiles[0].width < imageSize ||
          acceptedFiles[0].height < imageSize
        ) {
          setError("custom-file-too-small");
          return;
        }
        setInitialImage(acceptedFiles[0]);
        onImageAccept(acceptedFiles[0]);
        setImgName(acceptedFiles[0]?.name);
        setImgType(acceptedFiles[0]?.type);
        setOnceCropped(false);
      }
    }, []);

    const onDropRejected = useCallback((err) => {
      const error = err[0].errors[0];
      setError(error.code);
    }, []);

    const getFilesFromEvent = useCallback(async (event) => {
      const files = event.target.files || event.dataTransfer.files;
      const promises = [];
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const promise = new Promise((resolve) => {
          const image = new Image();
          let url: string;
          image.onload = function () {
            file.width = image.width;
            file.height = image.height;
            resolve(file);
          };
          url = URL.createObjectURL(file);
          image.src = url;
        });
        promises.push(promise);
      }
      return await Promise.all(promises);
    }, []);

    const { getRootProps, getInputProps, isDragReject, isDragActive } =
      useDropzone({
        onDrop,
        getFilesFromEvent,
        accept: "image/jpeg, image/png, image/gif",
        minSize: 1024,
        maxSize: 8388608,
        multiple: false,
        onDropRejected,
        preventDropOnDocument: true,
      });

    const dataDrop = useMemo(() => {
      if (error) return "reject";
      if (isDragReject) return "reject";
      if (isDragActive) return "active";
    }, [isDragActive, isDragReject, error]);

    const getCropData = () => {
      if (typeof cropper !== "undefined") {
        cropper.getCroppedCanvas().toBlob((file) => {
          const f = new File([file], imgName, { type: imgType });
          onImageAccept(f);
          setIsCrop(true);
          setOnceCropped(true);
        }, imgType);
      }
    };

    const declineOnCropper = () => {
      if (onceCropped) {
        setIsCrop(true);
      } else {
        onImageAccept(undefined);
        setImgName(undefined);
        setImgType(undefined);
        setIsCrop(false);
        setInitialImage(undefined);
      }
    };

    const changeCrop = () => {
      setIsCrop(false);
    };

    const declineImage = () => {
      onImageAccept(undefined);
      setImgName(undefined);
      setImgType(undefined);
      setIsCrop(false);
      setInitialImage(undefined);
    };

    if (isCrop) {
      return (
        <CroppedImage
          error={error}
          image={image}
          changeCrop={changeCrop}
          declineImage={declineImage}
        />
      );
    }

    if (image) {
      return (
        <CustomCropper
          error={error}
          setCropper={setCropper}
          declineOnCropper={declineOnCropper}
          getCropData={getCropData}
          imageSize={imageSize}
          initialImage={initialImage}
        />
      );
    }

    return (
      <CustomDropper
        error={error}
        dataDrop={dataDrop}
        getInputProps={getInputProps}
        getRootProps={getRootProps}
      />
    );
  }
);

const CustomDropper = observer(
  ({ getRootProps, getInputProps, dataDrop, error }) => {
    return (
      <div className={styles.Wrapper}>
        <div
          className={styles.DropAndCrop}
          {...getRootProps()}
          data-drop={dataDrop}
        >
          <input {...getInputProps()} />

          <div className={styles.DropAndCrop_Container}>
            <div className={styles.DropAndCrop_Container_Title}>
              Загрузите <span>или перетащите</span> фотографию{" "}
              <span>в данную область</span>
            </div>
            <div className={styles.DropAndCrop_Container_Subtitle}>
              JPG, GIF, PNG размером от 1 кб до 8 мб
            </div>
            <div className={styles.DropAndCrop_Container_Button}>
              Загрузить файл
            </div>
          </div>
        </div>
        {error && <div className={styles.Wrapper_Error}>{error}</div>}
      </div>
    );
  }
);

const CustomCropper = observer(
  ({
    imageSize,
    initialImage,
    setCropper,
    declineOnCropper,
    getCropData,
    error,
  }) => {
    return (
      <div className={styles.Wrapper}>
        <div className={styles.CropperContainer}>
          <Cropper
            initialAspectRatio={1}
            aspectRatio={1}
            viewMode={0}
            minCropBoxHeight={imageSize}
            minCropBoxWidth={imageSize}
            dragMode={"none"}
            background={true}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false}
            guides={true}
            src={URL.createObjectURL(initialImage)}
            className={styles.CropperContainer_Cropper}
            zoomOnWheel={false}
            zoomable={true}
            onInitialized={(instance) => {
              setCropper(instance);
            }}
          />
          <div className={styles.CropperContainer_Button}>
            <button
              className={styles.CropperContainer_Button_Decline}
              onClick={declineOnCropper}
            >
              Отмена
            </button>
            <button
              className={styles.CropperContainer_Button_Accept}
              onClick={getCropData}
            >
              Применить
            </button>
          </div>
        </div>
        {error && <div className={styles.Wrapper_Error}>{error}</div>}
      </div>
    );
  }
);

const CroppedImage = observer(({ image, declineImage, changeCrop, error }) => {
  return (
    <div className={styles.Wrapper}>
      <div className={styles.CroppedImageContainer}>
        <div className={styles.CroppedImageContainer_Image}>
          <div className={styles.CroppedImageContainer_Image_Container}>
            <img src={URL.createObjectURL(image)} alt="cropped image" />
            <div
              className={styles.CroppedImageContainer_Image_Container_Buttons}
            >
              <div
                onClick={declineImage}
                className={
                  styles.CroppedImageContainer_Image_Container_Buttons_Button
                }
              >
                <img src="/static/images/helpMe/Trash.svg" alt="Удалить" />
              </div>
              <div
                onClick={changeCrop}
                className={
                  styles.CroppedImageContainer_Image_Container_Buttons_Button
                }
              >
                <img
                  src="/static/images/helpMe/Pencil.svg"
                  alt="Редактировать"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {error && <div className={styles.Wrapper_Error}>{error}</div>}
    </div>
  );
});

export default DropAndCrop;
